import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { analyzeUrl, extractErrorMessages } from '@/api/brandingApi';
import type { BrandingAnalyzeResponse } from '@/api/brandingApi';

interface SiteFormState {
  siteName: string;
  siteUrl: string;
  sitemapUrl: string;
  siteTier: string;
}

type SitemapValidationState = {
  status: 'idle' | 'validating' | 'success' | 'error';
  validatedUrl?: string;
  result?: BrandingAnalyzeResponse;
  message?: string;
};

type OrganizationPlanTier = 'STARTER' | 'BASIC' | 'PRO' | 'AGENCY';
type SiteTier = 'STARTER' | 'BASIC' | 'PRO' | 'ENTERPRISE';

const ALLOWED_SITE_TIERS_BY_PLAN: Record<OrganizationPlanTier, SiteTier[]> = {
  STARTER: ['STARTER'],
  BASIC: ['BASIC'],
  PRO: ['BASIC', 'PRO'],
  AGENCY: ['BASIC', 'PRO', 'ENTERPRISE'],
};

const getAllowedSiteTiers = (planTier: OrganizationPlanTier): SiteTier[] => ALLOWED_SITE_TIERS_BY_PLAN[planTier];

const clampSiteTier = (planTier: OrganizationPlanTier, siteTier?: string): SiteTier => {
  const allowed = getAllowedSiteTiers(planTier);
  return (siteTier && allowed.includes(siteTier as SiteTier) ? siteTier : allowed[0]) as SiteTier;
};

const DEFAULT_SITE: SiteFormState = {
  siteName: '',
  siteUrl: '',
  sitemapUrl: '',
  siteTier: 'STARTER',
};

const initialSitemapValidation: SitemapValidationState = { status: 'idle' };
const SITEMAP_URL_FORMAT_MESSAGE = 'Use a lowercase sitemap URL ending with .xml, for example https://example.com/sitemap.xml.';

const isSitemapValidationCurrent = (state: SitemapValidationState, sitemapUrl: string) => (
  state.status === 'success' && state.validatedUrl === sitemapUrl.trim()
);

const validateSitemapUrlFormat = (sitemapUrl: string): string | null => {
  if (sitemapUrl !== sitemapUrl.toLowerCase() || !sitemapUrl.endsWith('.xml')) {
    return SITEMAP_URL_FORMAT_MESSAGE;
  }
  return null;
};

export const RegisterPage = () => {
  const brandLogoSrc = '/asneris.png';
  const navigate = useNavigate();
  const { register } = useAuthStore();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [planTier, setPlanTier] = useState<OrganizationPlanTier>('STARTER');
  const [additionalCustomerEmail, setAdditionalCustomerEmail] = useState('');
  const [sites, setSites] = useState<SiteFormState[]>([{ ...DEFAULT_SITE }]);
  const [sitemapValidations, setSitemapValidations] = useState<SitemapValidationState[]>([{ ...initialSitemapValidation }]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const allowedSiteTiers = getAllowedSiteTiers(planTier);
  const updateSite = (index: number, next: Partial<SiteFormState>) => {
    setSites((prev) => prev.map((site, i) => {
      if (i !== index) {
        return site;
      }
      const merged = { ...site, ...next };
      merged.siteTier = clampSiteTier(planTier, merged.siteTier);
      return merged;
    }));
    if (next.siteUrl !== undefined || next.sitemapUrl !== undefined) {
      setSitemapValidations((prev) => prev.map((validation, i) => (i === index ? { ...initialSitemapValidation } : validation)));
    }
  };

  const onPlanTierChange = (nextPlan: OrganizationPlanTier) => {
    setPlanTier(nextPlan);
    if (nextPlan !== 'PRO' && sites.length > 1) {
      setSites((prev) => [prev[0]]);
      setSitemapValidations((prev) => [prev[0] || { ...initialSitemapValidation }]);
    }
    setSites((prev) => prev.map((site) => ({ ...site, siteTier: clampSiteTier(nextPlan, site.siteTier) })));
  };

  const setSiteSitemapValidation = (index: number, next: SitemapValidationState) => {
    setSitemapValidations((prev) => prev.map((validation, i) => (i === index ? next : validation)));
  };

  const validateSiteSitemap = async (index: number) => {
    const site = sites[index];
    const trimmedSiteUrl = site.siteUrl.trim();
    const trimmedSitemapUrl = site.sitemapUrl.trim();

    if (!trimmedSiteUrl || !trimmedSitemapUrl) {
      setSiteSitemapValidation(index, { status: 'error', message: 'Enter the website URL and sitemap URL before validating.' });
      return;
    }

    const sitemapFormatError = validateSitemapUrlFormat(trimmedSitemapUrl);
    if (sitemapFormatError) {
      setSiteSitemapValidation(index, { status: 'error', message: sitemapFormatError });
      return;
    }

    try {
      new URL(trimmedSiteUrl);
      new URL(trimmedSitemapUrl);
    } catch {
      setSiteSitemapValidation(index, { status: 'error', message: 'Enter valid http:// or https:// URLs before validating.' });
      return;
    }

    setSiteSitemapValidation(index, { status: 'validating', validatedUrl: trimmedSitemapUrl });

    try {
      const result = await analyzeUrl({ url: trimmedSiteUrl, scanType: 'SITEMAP', sitemapUrl: trimmedSitemapUrl });
      const hasFailure = result.checks.some((check) => check.status === 'FAIL');
      setSiteSitemapValidation(index, {
        status: hasFailure ? 'error' : 'success',
        validatedUrl: trimmedSitemapUrl,
        result,
        message: hasFailure
          ? 'The sitemap responded, but validation found issues that should be reviewed before creating the workspace.'
          : 'Sitemap verified. The file exists and returned parseable sitemap content.',
      });
    } catch (err) {
      setSiteSitemapValidation(index, {
        status: 'error',
        validatedUrl: trimmedSitemapUrl,
        message: extractErrorMessages(err) || 'Unable to validate this sitemap URL.',
      });
    }
  };

  const validate = (): string | null => {
    if (!fullName.trim() || !email.trim() || !organizationName.trim()) {
      return 'Please complete all required organization fields';
    }

    for (let index = 0; index < sites.length; index += 1) {
      const site = sites[index];
      if (!site.siteName.trim() || !site.siteUrl.trim() || !site.sitemapUrl.trim()) {
        return 'Please complete all required site fields';
      }
      if (!site.siteUrl.startsWith('http://') && !site.siteUrl.startsWith('https://')) {
        return 'Each site URL must start with http:// or https://';
      }
      if (!site.sitemapUrl.startsWith('http://') && !site.sitemapUrl.startsWith('https://')) {
        return 'Each sitemap URL must start with http:// or https://';
      }
      const sitemapFormatError = validateSitemapUrlFormat(site.sitemapUrl.trim());
      if (sitemapFormatError) {
        return sitemapFormatError;
      }
      if (!allowedSiteTiers.includes(site.siteTier as SiteTier)) {
        return `Allowed site tiers for ${planTier} plan are: ${allowedSiteTiers.join(', ')}`;
      }
      if (!isSitemapValidationCurrent(sitemapValidations[index] || initialSitemapValidation, site.sitemapUrl)) {
        return 'Please validate each sitemap URL before creating the workspace';
      }
    }

    if (planTier !== 'PRO' && sites.length > 1) {
      return 'Multiple sites are allowed only for Pro plan';
    }

    if (planTier === 'PRO' && sites.length > 2) {
      return 'Pro plan supports a maximum of two sites during registration';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsCompleted(false);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await register({
        email: email.trim(),
        fullName: fullName.trim(),
        organizationName: organizationName.trim(),
        planTier,
        managedBy: 'SELF',
        sites: sites.map((site) => ({
          siteName: site.siteName.trim(),
          siteUrl: site.siteUrl.trim(),
          sitemapUrl: site.sitemapUrl.trim(),
          siteTier: clampSiteTier(planTier, site.siteTier),
        })),
        additionalCustomerEmail: additionalCustomerEmail.trim() || undefined,
      });
      setSuccess('Registration successful. Login credentials have been sent to your email.');
      setIsCompleted(true);
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#eef3fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 18px 45px rgba(9, 30, 66, 0.12)',
          backgroundColor: '#f9fbff',
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            sx={{
              display: { xs: 'none', md: 'block' },
              background: 'linear-gradient(180deg, #0f3f85 0%, #0e3773 100%)',
            }}
          >
            <Box sx={{ p: { xs: 2.2, md: 3 }, color: '#eaf4ff', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 3.2 }}>
                <Box
                  component="img"
                  src={brandLogoSrc}
                  alt="Asneris"
                  sx={{ width: 30, height: 30, objectFit: 'contain' }}
                />
                <Stack spacing={0.1}>
                  <Typography sx={{ color: '#ffffff', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                  <Typography variant="caption" sx={{ color: '#a8c6e9', letterSpacing: '0.12em', fontWeight: 700 }}>SEO/GEO ENGINE</Typography>
                  <Typography variant="caption" sx={{ color: '#d6e7ff', fontWeight: 600 }}>Technical SEO Platform</Typography>
                </Stack>
              </Stack>

              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1.3, fontSize: { md: 28, lg: 32 } }}>
                Improve Search & AI Discoverability
              </Typography>

              <Typography variant="body2" sx={{ color: '#d8e7fb', mb: 2.2 }}>
                Build your organization workspace in two simple steps.
              </Typography>

              <Stack spacing={1.7} sx={{ mt: 0.5, mb: 2.6, position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: 13, top: 10, bottom: 10, width: 2, bgcolor: 'rgba(193, 222, 255, 0.45)' }} />

                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ position: 'relative' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#78b7f7', color: '#0b2f63', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>1</Box>
                  <Stack>
                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: 'rgba(159, 210, 255, 0.2)', border: '1px solid rgba(159, 210, 255, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ApartmentRoundedIcon sx={{ color: '#9fd2ff', fontSize: 18 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#dcedff', fontWeight: 700 }}>Organization Details</Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ position: 'relative' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(210, 229, 251, 0.75)', color: '#385d90', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>2</Box>
                  <Stack>
                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: 'rgba(159, 210, 255, 0.12)', border: '1px solid rgba(159, 210, 255, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LanguageRoundedIcon sx={{ color: '#bbddff', fontSize: 18 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#dcedff', fontWeight: 700 }}>First Website</Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ position: 'relative' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(210, 229, 251, 0.75)', color: '#385d90', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#dcedff', fontWeight: 700 }}>Complete</Typography>
                </Stack>
              </Stack>

              <Stack spacing={1.05} sx={{ mb: 3 }}>
                {['Technical SEO', 'Analytics', 'Search Console', 'AI Search'].map((item) => (
                  <Stack key={item} direction="row" spacing={1} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: '#8fd0ff', fontSize: 18 }} />
                    <Typography sx={{ color: '#d8e7fb', fontWeight: 600 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Box
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(100, 160, 230, 0.2) 0%, rgba(52, 109, 177, 0.08) 100%)',
                  minHeight: 150,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <LanguageRoundedIcon sx={{ fontSize: 56, color: '#7eb8f0' }} />
              </Box>

              <Box sx={{ mt: 'auto', borderRadius: 1.8, p: 1.4, backgroundColor: 'rgba(181, 216, 255, 0.14)' }}>
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.7 }}>
                  <ShieldRoundedIcon sx={{ color: '#8fd0ff', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: '#e1efff', fontWeight: 700 }}>
                    Secure Platform
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', color: '#d1e6ff' }}>Encrypted connections</Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#d1e6ff' }}>Your data stays private</Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#d1e6ff' }}>Enterprise-grade security</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} lg={8}>
            <CardContent
              sx={{
                p: { xs: 2, md: 3.2, lg: 3.5 },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 700 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              <Stack direction="row" spacing={1.1} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mb: 1.2 }}>
                <Box
                  component="img"
                  src={brandLogoSrc}
                  alt="Asneris"
                  sx={{ width: 26, height: 26, objectFit: 'contain' }}
                />
                <Stack spacing={0.1}>
                  <Typography sx={{ color: '#1d457f', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                  <Typography variant="caption" sx={{ color: '#5577a8', fontWeight: 700 }}>SEO/GEO ENGINE</Typography>
                </Stack>
              </Stack>

              <Typography variant="h4" sx={{ color: '#163a70', fontWeight: 800, mb: 2.4, fontSize: { xs: 28, md: 34 }, textAlign: 'center' }}>
                Create Your Workspace
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h6" sx={{ color: '#1d3f72', fontWeight: 800, mb: 1.3 }}>
                  Organization Details
                </Typography>
                <Divider sx={{ mb: 1.7 }} />

                <Stack spacing={1.35}>
                  <TextField
                    label="Organization Name"
                    required
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                  />
                  <TextField
                    label="Business Email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    label="Administrator"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                  <TextField
                    label="Plan"
                    required
                    select
                    value={planTier}
                    onChange={(e) => onPlanTierChange(e.target.value as OrganizationPlanTier)}
                  >
                    <MenuItem value="STARTER">Starter</MenuItem>
                    <MenuItem value="BASIC">Basic</MenuItem>
                    <MenuItem value="PRO">Pro</MenuItem>
                    <MenuItem value="AGENCY">Agency</MenuItem>
                  </TextField>
                  <TextField
                    label="Additional Email (Optional)"
                    value={additionalCustomerEmail}
                    onChange={(e) => setAdditionalCustomerEmail(e.target.value)}
                  />
                </Stack>

                <Typography variant="h6" sx={{ color: '#1d3f72', fontWeight: 800, mt: 2.5, mb: 1.3 }}>
                  Website Details
                </Typography>
                <Divider sx={{ mb: 1.7 }} />

                <Stack spacing={1.35} sx={{ mb: 0.4 }}>
                  {sites.map((site, index) => (
                    <Box key={index}>
                      <TextField
                        fullWidth
                        label="Website Name"
                        required
                        value={site.siteName}
                        onChange={(e) => updateSite(index, { siteName: e.target.value })}
                        sx={{ mb: 1.35 }}
                      />
                      <TextField
                        fullWidth
                        label="Website URL"
                        required
                        value={site.siteUrl}
                        onChange={(e) => updateSite(index, { siteUrl: e.target.value })}
                        placeholder="https://yourwebsite.com"
                        sx={{ mb: 1.35 }}
                      />
                      <TextField
                        fullWidth
                        label="Sitemap URL"
                        required
                        value={site.sitemapUrl}
                        onChange={(e) => updateSite(index, { sitemapUrl: e.target.value })}
                        placeholder="https://yourwebsite.com/sitemap.xml"
                        helperText="Use a lowercase .xml sitemap URL."
                        sx={{ mb: 1.35 }}
                      />
                      <Box sx={{ border: '1px solid', borderColor: isSitemapValidationCurrent(sitemapValidations[index] || initialSitemapValidation, site.sitemapUrl) ? 'success.light' : 'divider', borderRadius: 2, p: 1.5, mb: 1.35, bgcolor: '#ffffff' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1d3f72' }}>Sitemap source check</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Validate and review the XML file before creating the workspace.
                            </Typography>
                          </Box>
                          <Button
                            variant={isSitemapValidationCurrent(sitemapValidations[index] || initialSitemapValidation, site.sitemapUrl) ? 'outlined' : 'contained'}
                            size="small"
                            onClick={() => validateSiteSitemap(index)}
                            disabled={isSubmitting || sitemapValidations[index]?.status === 'validating' || !site.siteUrl.trim() || !site.sitemapUrl.trim()}
                            startIcon={sitemapValidations[index]?.status === 'validating' ? <CircularProgress size={14} /> : isSitemapValidationCurrent(sitemapValidations[index] || initialSitemapValidation, site.sitemapUrl) ? <CheckCircleRoundedIcon /> : undefined}
                          >
                            {sitemapValidations[index]?.status === 'validating'
                              ? 'Validating...'
                              : isSitemapValidationCurrent(sitemapValidations[index] || initialSitemapValidation, site.sitemapUrl)
                                ? 'Validated'
                                : 'Validate sitemap'}
                          </Button>
                        </Stack>
                        {site.sitemapUrl.trim() && (
                          <Link href={site.sitemapUrl.trim()} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'inline-block', mt: 1, wordBreak: 'break-all' }}>
                            Open sitemap XML for review
                          </Link>
                        )}
                        {sitemapValidations[index]?.status === 'success' && sitemapValidations[index]?.result && (
                          <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{sitemapValidations[index]?.message}</Typography>
                            <Typography variant="caption" display="block">
                              {sitemapValidations[index]?.result?.domain} · Score {sitemapValidations[index]?.result?.overallScore}/100 · {sitemapValidations[index]?.result?.checks.length || 0} checks
                            </Typography>
                          </Alert>
                        )}
                        {sitemapValidations[index]?.status === 'error' && (
                          <Alert severity="error" icon={<ErrorOutlineRoundedIcon />} sx={{ mt: 1 }}>
                            {sitemapValidations[index]?.message || 'Sitemap validation failed.'}
                          </Alert>
                        )}
                      </Box>
                      <TextField
                        fullWidth
                        label="Site Tier"
                        required
                        select
                        value={clampSiteTier(planTier, site.siteTier)}
                        disabled={allowedSiteTiers.length === 1}
                        onChange={(e) => updateSite(index, { siteTier: e.target.value })}
                      >
                        {allowedSiteTiers.map((tier) => (
                          <MenuItem key={tier} value={tier}>{tier}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ))}
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  endIcon={!isSubmitting ? <ArrowForwardRoundedIcon /> : undefined}
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    mt: 2.2,
                    height: 54,
                    background: 'linear-gradient(90deg, #1266e3 0%, #0a52c7 100%)',
                    fontWeight: 800,
                    fontSize: 15,
                    boxShadow: '0 12px 24px rgba(18, 102, 227, 0.3)',
                    '&:hover': {
                      boxShadow: '0 15px 28px rgba(18, 102, 227, 0.38)',
                    },
                  }}
                >
                  {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Workspace'}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                  By creating an account, you agree to our{' '}
                  <Link component="button" variant="caption">Terms of Service</Link> and{' '}
                  <Link component="button" variant="caption">Privacy Policy</Link>.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2.1, textAlign: 'center' }}>
                  Already have an account?{' '}
                  <Link component="button" variant="body2" sx={{ fontWeight: 700 }} onClick={() => navigate('/login')}>Sign in</Link>
                </Typography>

                {isCompleted && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main', fontWeight: 700, textAlign: 'center' }}>
                    Workspace created successfully. Redirecting to sign in...
                  </Typography>
                )}
              </Box>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};
